import { useQuery } from '@tanstack/react-query'
import { profileApi, barterApi, reviewApi } from '@/services/api'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth.store'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import Loading from '@/components/ui/Loading'
import ReviewSection from '@/components/reviews/ReviewSection'
import { formatDate } from '@/utils/format'
import { ArrowLeft, Mail, MapPin, Briefcase, Star } from 'lucide-react'
import type { Barter, Review } from '@/types'

export default function UserProfile() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user: currentUser } = useAuthStore()

  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile', id],
    queryFn: () => profileApi.getUser(Number(id)),
    enabled: !!id,
  })

  const { data: bartersData, isLoading: bartersLoading } = useQuery({
    queryKey: ['user-barters', id],
    queryFn: () => barterApi.list({}),
    enabled: !!id,
  })

  const { data: reviewsData } = useQuery({
    queryKey: ['user-reviews', id],
    queryFn: () => reviewApi.list({ reviewee_id: Number(id) }),
    enabled: !!id,
  })

  if (profileLoading) return <Loading />

  const user = profileData?.data?.user
  const userBarters = bartersData?.data?.barters?.filter(
    (b: Barter) => b.user_id === Number(id) && b.visibility === 'active'
  ) || []
  const reviews = reviewsData?.data?.reviews || []

  const averageRating = reviews.length > 0
    ? reviews.reduce((sum: number, r: Review) => sum + r.rating, 0) / reviews.length
    : 0

  const isOwnProfile = currentUser?.id === Number(id)

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">User not found</p>
        <Button onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {isOwnProfile && (
          <Button onClick={() => navigate('/dashboard/profile')}>
            Edit Profile
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Information */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <div className="text-center">
              <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl font-bold text-primary-600">
                  {user.first_name?.[0]}{user.last_name?.[0]}
                </span>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                {user.first_name} {user.last_name}
              </h1>
              {isOwnProfile && (
                <div className="mb-3">
                  <Badge variant="primary">You</Badge>
                </div>
              )}
              <div className="flex items-center justify-center space-x-2 text-gray-600 text-sm mb-3">
                <Badge variant={user.user_type === 'professional' ? 'success' : 'gray'}>
                  {user.user_type === 'professional' ? 'Professional' : user.user_type}
                </Badge>
                {user.subscription_tier === 'paid' && user.subscription_status === 'active' && (
                  <Badge variant="warning">Pro Member</Badge>
                )}
              </div>

              {reviews.length > 0 && (
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  <span className="text-xl font-semibold">{averageRating.toFixed(1)}</span>
                  <span className="text-gray-500 text-sm">({reviews.length} reviews)</span>
                </div>
              )}
            </div>

            <div className="border-t mt-6 pt-6 space-y-4">
              {user.email && (
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Email</p>
                    <p className="text-sm text-gray-900">{user.email}</p>
                  </div>
                </div>
              )}

              {user.phone_number && (
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Phone</p>
                    <p className="text-sm text-gray-900">{user.phone_number}</p>
                  </div>
                </div>
              )}

              {(user.city || user.country) && (
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Location</p>
                    <p className="text-sm text-gray-900">
                      {user.city && `${user.city}, `}
                      {user.country}
                      {user.continent && (
                        <>
                          <br />
                          <span className="text-gray-500">{user.continent}</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {user.profession && (
                <div className="flex items-start space-x-3">
                  <Briefcase className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Profession</p>
                    <p className="text-sm text-gray-900">{user.profession}</p>
                  </div>
                </div>
              )}


              <div className="text-xs text-gray-500 pt-4 border-t">
                Member since {formatDate(user.created_at)}
              </div>
            </div>
          </Card>
        </div>

        {/* User's Barters and Reviews */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active Barters */}
          <Card title={`${isOwnProfile ? 'Your' : user.first_name + "'s"} Active Barters`}>
            {bartersLoading ? (
              <Loading />
            ) : userBarters.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No active barters at the moment
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {userBarters.map((barter: Barter) => (
                  <div
                    key={barter.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/dashboard/barters/${barter.id}`)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{barter.title}</h4>
                      <Badge variant={barter.type === 'goods' ? 'primary' : 'success'}>
                        {barter.type}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {barter.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <Badge status={barter.status}>{barter.status}</Badge>
                      <span className="text-sm text-gray-500">
                        {formatDate(barter.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Reviews */}
          <Card>
            <ReviewSection type="user" id={Number(id)} allowReview={!isOwnProfile} />
          </Card>
        </div>
      </div>
    </div>
  )
}
